import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import { approxFrameSeconds, formatFrameTime, storyboardFrameUrl } from 'src/utils/storyboard';
import { useAssetThumbnailFromStoryboardMutation } from 'src/api/mutations/assetContentAccessMutations';
import { useAlerts } from 'src/common/AlertManager';
import Button from 'src/components/Button';
import StoryboardFrame from './StoryboardFrame';
import { StoryboardSource, thumbUrl, useFrameLabel } from './StoryboardStrip';
import messages from './messages';
import styles from './Storyboard.module.scss';

const MIN_CELL_WIDTH = 180;
const GAP = 8;
const LABEL_HEIGHT = 22;
const LARGE_WIDTH = 960;
// Rows rendered beyond the viewport each way: enough that a slow scroll never
// shows an empty row, few enough that the request count stays well under the
// storyboard route's per-IP burst of 60.
const OVERSCAN_ROWS = 2;

type RowData = {
  source: StoryboardSource;
  columns: number;
  highlight: number | null;
  loaded: Set<number>;
  frameLabel: (frame: number) => string;
  onOpen: (frame: number) => void;
};

/** One grid row. Module-level so react-window doesn't remount rows on every render. */
const GridRow: React.FC<ListChildComponentProps<RowData>> = ({ index, style, isScrolling, data }) => {
  const { source, columns, highlight, loaded, frameLabel, onOpen } = data;
  const first = index * columns + 1;
  const frames = Array.from({ length: columns }, (_, i) => first + i).filter((f) => f <= source.count);
  return (
    <div
      className={styles.gridRow}
      style={{ ...style, gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, columnGap: GAP }}
    >
      {frames.map((frame) => (
        <StoryboardFrame
          key={frame}
          frame={frame}
          src={thumbUrl(source, frame)}
          aspect={source.aspect}
          label={frameLabel(frame)}
          // Hold back NEW loads while the list is flung; frames already seen
          // come from the browser cache and keep showing.
          deferLoad={!!isScrolling && !loaded.has(frame)}
          highlighted={highlight === frame}
          onLoad={() => loaded.add(frame)}
          onClick={() => onOpen(frame)}
        />
      ))}
    </div>
  );
};

export type StoryboardViewerProps = StoryboardSource & {
  /** Offer "Set as thumbnail" on the large view (callers gate on role). */
  canSetThumbnail?: boolean;
  /** Open straight on this frame's large view. */
  initialFrame?: number;
};

/**
 * Every storyboard frame (#958 follow-up). Long videos run to thousands of
 * frames, so the grid is virtualised: only the rows on screen (plus a couple
 * either side) exist, and only those load.
 */
export const StoryboardViewer: React.FC<StoryboardViewerProps> = ({ initialFrame, canSetThumbnail = false, ...source }) => {
  const { formatMessage } = useIntl();
  const { showInfo, showAlert } = useAlerts();
  // The frame set as thumbnail during this visit. The backend doesn't record
  // which frame a thumbnail came from, so this can't show an earlier choice.
  const [thumbnailFrame, setThumbnailFrame] = useState<number | null>(null);
  const thumbnailMutation = useAssetThumbnailFromStoryboardMutation(source.assetId, {
    onSuccess: (_asset, frame) => {
      setThumbnailFrame(frame);
      showInfo(formatMessage(messages.thumbnailSet, { frame }));
    },
    onError: () => showAlert(formatMessage(messages.thumbnailFailed)),
  });
  const frameLabel = useFrameLabel(source);
  const [single, setSingle] = useState<number | null>(initialFrame ?? null);
  const [highlight, setHighlight] = useState<number | null>(initialFrame ?? null);
  const [goTo, setGoTo] = useState('');
  const [width, setWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<FixedSizeList>(null);
  const loaded = useRef(new Set<number>()).current;

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.clientWidth || 720);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const columns = Math.max(2, Math.floor((width + GAP) / (MIN_CELL_WIDTH + GAP)));
  const cellWidth = (width - GAP * (columns - 1)) / columns;
  const rowHeight = Math.round(cellWidth / source.aspect + LABEL_HEIGHT + GAP);
  const rowCount = Math.ceil(source.count / columns);
  const listHeight = Math.min(rowCount * rowHeight, Math.round(window.innerHeight * 0.6));
  const rowOf = (frame: number) => Math.floor((frame - 1) / columns);

  const largeUrl = (frame: number) =>
    storyboardFrameUrl({
      assetId: source.assetId,
      frame,
      width: LARGE_WIDTH,
      height: Math.round(LARGE_WIDTH / source.aspect),
      jwt: source.jwt,
    });

  const clamp = (frame: number) => Math.min(source.count, Math.max(1, frame));

  const showGridAt = (frame: number) => {
    setHighlight(frame);
    setSingle(null);
  };

  const handleGoTo = (e: React.FormEvent) => {
    e.preventDefault();
    const n = parseInt(goTo, 10);
    if (Number.isNaN(n)) return;
    const frame = clamp(n);
    setGoTo(String(frame));
    if (single !== null) {
      setSingle(frame);
      setHighlight(frame);
      return;
    }
    setHighlight(frame);
    listRef.current?.scrollToItem(rowOf(frame), 'center');
  };

  // Arrow keys step, Escape returns to the grid — single view only.
  useEffect(() => {
    if (single === null) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
      if (e.key === 'ArrowRight') setSingle((f) => clamp((f ?? 1) + 1));
      else if (e.key === 'ArrowLeft') setSingle((f) => clamp((f ?? 1) - 1));
      else if (e.key === 'Escape') showGridAt(single);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [single, source.count]);

  // Warm the neighbours so stepping through feels instant.
  useEffect(() => {
    if (single === null) return;
    [single - 1, single + 1].filter((f) => f >= 1 && f <= source.count).forEach((f) => {
      const img = new Image();
      img.src = largeUrl(f);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [single]);

  const openSingle = useCallback((frame: number) => {
    setHighlight(frame);
    setSingle(frame);
  }, []);

  const rowData: RowData = useMemo(
    () => ({ source, columns, highlight, loaded, frameLabel, onOpen: openSingle }),
    // `source` is spread fresh each render; its fields are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source.assetId, source.count, source.aspect, source.jwt, source.durationSeconds, source.mode, columns, highlight],
  );

  const showTimes = !!source.durationSeconds && source.mode !== 'keyframes';
  const singleSeconds =
    single === null ? null : approxFrameSeconds(single, source.count, source.durationSeconds, source.mode);

  return (
    <div className={styles.viewer} data-test="storyboard-viewer">
      <header className={styles.viewerHeader}>
        <div>
          <h3>{formatMessage(messages.viewerTitle)}</h3>
          <span className={styles.viewerMeta}>
            {formatMessage(messages.viewerCount, { count: source.count })}
            {showTimes && ` · ${formatMessage(messages.timesApproximate)}`}
          </span>
        </div>
        <form className={styles.goTo} onSubmit={handleGoTo}>
          <label htmlFor="storyboard-go-to">{formatMessage(messages.goToFrame)}</label>
          <input
            id="storyboard-go-to"
            type="number"
            min={1}
            max={source.count}
            value={goTo}
            onChange={(e) => setGoTo(e.target.value)}
            data-test="storyboard-go-to"
          />
          <button type="submit" className={styles.linkButton}>
            {formatMessage(messages.go)}
          </button>
        </form>
      </header>

      <div ref={containerRef} className={styles.viewerBody}>
        {single !== null ? (
          <div className={styles.single} data-test="storyboard-single">
            <div className={styles.singleToolbar}>
              <button type="button" className={styles.linkButton} onClick={() => showGridAt(single)}>
                ← {formatMessage(messages.backToGrid)}
              </button>
              <span>
                {formatMessage(messages.singleFramePosition, { frame: single, count: source.count })}
                {singleSeconds !== null && ` · ~${formatFrameTime(singleSeconds)}`}
              </span>
              <span className={styles.viewerMeta}>{formatMessage(messages.keyboardHint)}</span>
              {canSetThumbnail &&
                (thumbnailFrame === single && !thumbnailMutation.isLoading ? (
                  <span className={styles.thumbnailBadge} data-test="storyboard-is-thumbnail">
                    {formatMessage(messages.isThumbnail)}
                  </span>
                ) : (
                  <Button
                    mode="secondary"
                    size="small"
                    className={styles.setThumbnail}
                    loading={thumbnailMutation.isLoading}
                    disabled={thumbnailMutation.isLoading}
                    onClick={() => thumbnailMutation.mutate(single)}
                    datatest="storyboard-set-thumbnail"
                  >
                    {formatMessage(
                      thumbnailMutation.isLoading ? messages.settingThumbnail : messages.setAsThumbnail,
                    )}
                  </Button>
                ))}
            </div>
            <div className={styles.singleStage}>
              <button
                type="button"
                className={styles.stepButton}
                onClick={() => setSingle(clamp(single - 1))}
                disabled={single <= 1}
                aria-label={formatMessage(messages.previous)}
              >
                ‹
              </button>
              <StoryboardFrame frame={single} src={largeUrl(single)} aspect={source.aspect} />
              <button
                type="button"
                className={styles.stepButton}
                onClick={() => setSingle(clamp(single + 1))}
                disabled={single >= source.count}
                aria-label={formatMessage(messages.next)}
              >
                ›
              </button>
            </div>
          </div>
        ) : (
          width > 0 && (
            <FixedSizeList
              ref={listRef}
              height={listHeight}
              width={width}
              itemCount={rowCount}
              itemSize={rowHeight}
              itemData={rowData}
              overscanCount={OVERSCAN_ROWS}
              useIsScrolling
              // Coming back from the large view lands on the frame left there.
              initialScrollOffset={
                highlight ? Math.max(0, rowOf(highlight) * rowHeight - listHeight / 2 + rowHeight / 2) : 0
              }
            >
              {GridRow}
            </FixedSizeList>
          )
        )}
      </div>
    </div>
  );
};

export default StoryboardViewer;
